import { LOCALE_META, type Locale } from './locales';

export type { Locale };
export type MessageBundle = Record<string, string>;

type Node =
  | { type: 'text'; value: string }
  | { type: 'pound' }
  | { type: 'arg'; name: string; kind: 'simple' }
  | { type: 'arg'; name: string; kind: 'number' | 'date' | 'time'; style?: string }
  | { type: 'arg'; name: string; kind: 'plural' | 'selectordinal' | 'select'; options: Record<string, Node[]> };


/** BCP-47 tag used for Intl formatters. */
export function intlLocale(locale: Locale | string): string {
  return LOCALE_META[locale as Locale]?.intl ?? locale;
}

const cache = new Map<string, Node[]>();

function parse(pattern: string, start: number, inPlural: boolean, nested: boolean): { nodes: Node[]; pos: number } {
  const nodes: Node[] = [];
  let text = '';
  let pos = start;
  const flush = (): void => {
    if (text) nodes.push({ type: 'text', value: text });
    text = '';
  };
  while (pos < pattern.length) {
    const ch = pattern[pos] ?? '';
    if (ch === "'") {
      const next = pattern[pos + 1];
      if (next === "'") {
        text += "'";
        pos += 2;
        continue;
      }
      if (next === '{' || next === '}' || (inPlural && next === '#')) {
        pos += 1;
        while (pos < pattern.length) {
          if (pattern[pos] === "'") {
            if (pattern[pos + 1] === "'") {
              text += "'";
              pos += 2;
              continue;
            }
            pos += 1;
            break;
          }
          text += pattern[pos];
          pos += 1;
        }
        continue;
      }
      text += "'";
      pos += 1;
      continue;
    }
    if (ch === '{') {
      flush();
      const arg = parseArgument(pattern, pos + 1);
      nodes.push(arg.node);
      pos = arg.pos;
      continue;
    }
    if (ch === '}') {
      if (nested) {
        flush();
        return { nodes, pos: pos + 1 };
      }
      text += ch;
      pos += 1;
      continue;
    }
    if (ch === '#' && inPlural) {
      flush();
      nodes.push({ type: 'pound' });
      pos += 1;
      continue;
    }
    text += ch;
    pos += 1;
  }
  flush();
  return { nodes, pos };
}

function readUntil(pattern: string, pos: number, stops: string[]): { value: string; pos: number } {
  let value = '';
  while (pos < pattern.length && !stops.includes(pattern[pos] ?? '')) {
    value += pattern[pos];
    pos += 1;
  }
  return { value, pos };
}

function parseArgument(pattern: string, start: number): { node: Node; pos: number } {
  const nameRead = readUntil(pattern, start, [',', '}']);
  const name = nameRead.value.trim();
  let pos = nameRead.pos;
  if (pattern[pos] === '}' || pos >= pattern.length) {
    return { node: { type: 'arg', name, kind: 'simple' }, pos: pos + 1 };
  }
  pos += 1; // skip ','
  const kindRead = readUntil(pattern, pos, [',', '}']);
  const kind = kindRead.value.trim();
  pos = kindRead.pos;
  if (kind === 'number' || kind === 'date' || kind === 'time') {
    let style: string | undefined;
    if (pattern[pos] === ',') {
      const styleRead = readUntil(pattern, pos + 1, ['}']);
      style = styleRead.value.trim() || undefined;
      pos = styleRead.pos;
    }
    const node: Node = style ? { type: 'arg', name, kind, style } : { type: 'arg', name, kind };
    return { node, pos: pos + 1 };
  }
  if (kind === 'plural' || kind === 'select' || kind === 'selectordinal') {
    const options: Record<string, Node[]> = {};
    if (pattern[pos] === ',') pos += 1;
    while (pos < pattern.length) {
      while (pos < pattern.length && /\s/.test(pattern[pos] ?? '')) pos += 1;
      if (pattern[pos] === '}') {
        pos += 1;
        break;
      }
      const selectorRead = readUntil(pattern, pos, ['{', '}']);
      const selector = selectorRead.value.trim();
      pos = selectorRead.pos;
      if (pattern[pos] !== '{') break;
      const inner = parse(pattern, pos + 1, kind !== 'select', true);
      options[selector] = inner.nodes;
      pos = inner.pos;
    }
    return { node: { type: 'arg', name, kind, options }, pos };
  }
  // Unknown type: treat as a simple argument and skip to the closing brace.
  const rest = readUntil(pattern, pos, ['}']);
  return { node: { type: 'arg', name, kind: 'simple' }, pos: rest.pos + 1 };
}

export function parseMessage(pattern: string): Node[] {
  const cached = cache.get(pattern);
  if (cached) return cached;
  const nodes = parse(pattern, 0, false, false).nodes;
  if (cache.size > 2000) cache.clear();
  cache.set(pattern, nodes);
  return nodes;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  if (typeof value === 'bigint') return Number(value);
  return Number.NaN;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number' || typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatNumberValue(value: unknown, locale: Locale, style?: string): string {
  const n = toNumber(value);
  if (Number.isNaN(n)) return String(value ?? '');
  const tag = intlLocale(locale);
  if (style === 'percent') return new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 1 }).format(n);
  if (style === 'integer') return new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }).format(n);
  if (style?.startsWith('currency')) {
    const currency = style.split(':')[1]?.trim() || 'TRY';
    return new Intl.NumberFormat(tag, { style: 'currency', currency }).format(n);
  }
  return new Intl.NumberFormat(tag).format(n);
}

type DateStyle = 'short' | 'medium' | 'long' | 'full';

function asDateStyle(style: string | undefined, fallback: DateStyle): DateStyle {
  return style === 'short' || style === 'medium' || style === 'long' || style === 'full' ? style : fallback;
}

export function formatDateValue(value: unknown, locale: Locale, style?: string): string {
  const d = toDate(value);
  if (!d) return String(value ?? '');
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: asDateStyle(style, 'medium') }).format(d);
}

export function formatTimeValue(value: unknown, locale: Locale, style?: string): string {
  const d = toDate(value);
  if (!d) return String(value ?? '');
  return new Intl.DateTimeFormat(intlLocale(locale), { timeStyle: asDateStyle(style, 'short') }).format(d);
}

function selectPlural(value: number, options: Record<string, Node[]>, locale: Locale, ordinal: boolean): Node[] | undefined {
  const exact = options[`=${value}`];
  if (exact) return exact;
  let category = 'other';
  try {
    category = new Intl.PluralRules(intlLocale(locale), { type: ordinal ? 'ordinal' : 'cardinal' }).select(value);
  } catch {
    category = value === 1 ? 'one' : 'other';
  }
  return options[category] ?? options.other;
}

function render(nodes: Node[], params: Record<string, unknown>, locale: Locale, pound: string | null): string {
  let out = '';
  for (const node of nodes) {
    switch (node.type) {
      case 'text':
        out += node.value;
        break;
      case 'pound':
        out += pound ?? '#';
        break;
      case 'arg': {
        const value = params[node.name];
        if (node.kind === 'simple') {
          out += value === undefined || value === null ? `{${node.name}}` : String(value);
        } else if (node.kind === 'number') {
          out += formatNumberValue(value, locale, node.style);
        } else if (node.kind === 'date') {
          out += formatDateValue(value, locale, node.style);
        } else if (node.kind === 'time') {
          out += formatTimeValue(value, locale, node.style);
        } else if (node.kind === 'select') {
          const key = value === undefined || value === null ? 'other' : String(value);
          const branch = node.options[key] ?? node.options.other ?? [];
          out += render(branch, params, locale, pound);
        } else if (node.kind === 'plural' || node.kind === 'selectordinal') {
          const n = toNumber(value);
          const branch = Number.isNaN(n) ? node.options.other : selectPlural(n, node.options, locale, node.kind === 'selectordinal');
          out += render(branch ?? [], params, locale, Number.isNaN(n) ? String(value ?? '') : formatNumberValue(n, locale));
        }
        break;
      }
      default:
        break;
    }
  }
  return out;
}

/**
 * ICU-lite message formatting: `{name}`, `{count, plural, =0 {none} one {# item} other {# items}}`,
 * `{gender, select, male {..} other {..}}`, `{n, number}`/`{n, number, percent|integer|currency:EUR}`,
 * `{d, date, short|medium|long|full}`, `{t, time, short|medium}`, nested arguments inside options and
 * apostrophe escaping (`'{'`, `''`). Missing simple params are left as `{name}`.
 */
export function formatMessage(pattern: string, params: Record<string, unknown> = {}, locale: Locale = 'en'): string {
  return render(parseMessage(pattern), params, locale, null);
}
